import { type JobsApi } from '@/features/jobs/api/job-api'
import { collection } from '@/infra/collection'
import { JobTicket } from './JobTicket'
import { Id } from '@/infra/id'
import { GetLatestOpenTicketsForJobsRequest } from '@taxfyle/api-internal/internal/ticket_pb'
import DataLoader from 'dataloader'
import { isNotNil } from '@/utils/object-util'
import { mapLatestOpenTicketProtoToProps } from './job-ticket-mappers'

/**
 * Store for job tickets.
 */
export class JobTicketStore {
  private readonly _ticketsLoader = new DataLoader(
    this._fetchLatestTicketsForJobs.bind(this),
    {
      cache: true,
      batch: true,
    },
  )

  /**
   * Tickets for jobs.
   */
  readonly jobTickets = collection({
    getDataId: (d) => `${d.jobId}/${d.id}`,
    getModelId: (m) => `${m.jobId}/${m.id}`,
    create: () => new JobTicket(),
  })

  constructor(private readonly _api: JobsApi) {
    this.fetchLatestOpenTicketsForJobs =
      this.fetchLatestOpenTicketsForJobs.bind(this)
    this.getLatestTicketForJob = this.getLatestTicketForJob.bind(this)
  }

  /**
   * Returns the latest ticket for the given job, if any.
   *
   * @param jobId
   */
  getLatestTicketForJob(jobId: Id) {
    return this.jobTickets.find((t) => t.jobId === jobId) ?? null
  }

  /**
   * Fetches the latest open tickets for the given job IDs.
   * @param jobIds
   */
  async fetchLatestOpenTicketsForJobs(
    jobIds: readonly Id[],
    signal?: AbortSignal,
  ) {
    const ids = jobIds.filter(Id.some)
    const tickets = await Promise.all(
      ids.map((id) => this._ticketsLoader.load(id)),
    )
    signal?.throwIfAborted()
    return tickets.filter(isNotNil)
  }

  private async _fetchLatestTicketsForJobs(ids: readonly string[]) {
    const request = new GetLatestOpenTicketsForJobsRequest()
    request.setJobIdsList(ids.slice())
    const response =
      await this._api.tickets.getLatestOpenTicketsForJobs(request)

    this.jobTickets.set(
      Array.from(response.getLatestOpenTicketsMap().entries()).map(
        ([jobId, latestOpenTicket]) =>
          mapLatestOpenTicketProtoToProps(latestOpenTicket, Id.assert(jobId)),
      ),
    )
    return ids.map(this.getLatestTicketForJob)
  }
}
